import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Button,
  Input,
  Typography,
  Alert,
  Flex,
  Space,
} from 'antd';
import { MailOutlined, CloseOutlined } from '@ant-design/icons';
import DashboardService from '../../service';

const { Text } = Typography;

const VerificationModal = ({ userId, onSuccess, onClose, open }) => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(5);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(600);
  const inputRefs = useRef([]);

  useEffect(() => {
    if (open) {
      setCode(['', '', '', '', '', '']);
      setError('');
      setAttemptsRemaining(5);
      setTimeRemaining(600);
      setResendSuccess(false);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [open]);

  useEffect(() => {
    if (!open || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newCode.every((digit) => digit !== '') && index === 5) {
      handleVerify(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    if (!/^\d+$/.test(pastedData)) return;

    const newCode = pastedData.split('');
    while (newCode.length < 6) newCode.push('');

    setCode(newCode);

    const lastIndex = Math.min(newCode.length - 1, 5);
    inputRefs.current[lastIndex]?.focus();

    if (newCode.every((digit) => digit !== '')) {
      handleVerify(newCode.join(''));
    }
  };

  const handleVerify = async (codeString = code.join('')) => {
    if (codeString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    if (timeRemaining <= 0) {
      setError('Code has expired. Please login again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await DashboardService.verifyCode(userId, codeString);

      if (result.success) {
        onSuccess(result);
      } else {
        setError(result.error || 'Invalid verification code');

        if (result.attemptsRemaining !== undefined) {
          setAttemptsRemaining(result.attemptsRemaining);

          if (result.attemptsRemaining === 0) {
            setTimeout(() => {
              onClose();
            }, 2000);
          }
        }

        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      const result = await DashboardService.resendCode(userId);

      if (result.success) {
        setResendSuccess(true);
        setTimeRemaining(600);
        setAttemptsRemaining(5);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();

        setTimeout(() => setResendSuccess(false), 3000);
      } else {
        setError(result.error || 'Failed to resend code');
      }
    } catch (err) {
      console.error('Resend error:', err);
      setError('Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      closable={false}
      styles={{
        body: { padding: 24 },
      }}
    >
      <Flex justify="space-between" align="center" style={{ marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid var(--ant-color-border)' }}>
        <Flex align="center" gap={8}>
          <MailOutlined style={{ color: 'var(--ant-color-primary)' }} />
          <Text strong style={{ fontSize: 16 }}>Email Verification</Text>
        </Flex>
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onClose}
          size="small"
        />
      </Flex>

      <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 24 }}>
        We've sent a 6-digit verification code to your email.
        Please enter it below to complete your login.
      </Text>

      <Flex
        gap={8}
        justify="center"
        style={{ marginBottom: 24 }}
        onPaste={handlePaste}
      >
        {code.map((digit, index) => (
          <Input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={loading || timeRemaining <= 0}
            style={{
              width: 56,
              height: 64,
              fontSize: '1.5rem',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
            autoFocus={index === 0}
          />
        ))}
      </Flex>

      <Flex
        justify="space-between"
        style={{
          marginBottom: 24,
          padding: 16,
          backgroundColor: 'var(--ant-color-fill-quaternary)',
          borderRadius: 8,
        }}
      >
        <Text type="secondary">
          ⏱️ Code expires in: <strong>{formatTime(timeRemaining)}</strong>
        </Text>
        <Text type="secondary">
          Attempts remaining: <strong>{attemptsRemaining}/5</strong>
        </Text>
      </Flex>

      {error && (
        <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      {resendSuccess && (
        <Alert
          message="✅ New code sent to your email!"
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Button
        type="primary"
        block
        size="large"
        loading={loading}
        onClick={() => handleVerify()}
        disabled={code.some((d) => !d) || timeRemaining <= 0}
        style={{ marginBottom: 16 }}
      >
        {loading ? 'Verifying...' : 'Verify Code'}
      </Button>

      <Flex
        vertical
        align="center"
        style={{ paddingTop: 16, borderTop: '1px solid var(--ant-color-border)' }}
      >
        <Text type="secondary" style={{ marginBottom: 8 }}>
          Didn't receive the code?
        </Text>
        <Button
          type="link"
          onClick={handleResend}
          disabled={resendLoading || timeRemaining <= 0}
        >
          {resendLoading ? 'Sending...' : 'Resend Code'}
        </Button>
      </Flex>
    </Modal>
  );
};

export default VerificationModal;
